import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  Avatar,
  CircularProgress,
  Fade,
  Slide,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  CheckCircle,
  Shield,
  TrendingUp,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { loginApi, getProfileApi } from "../api/auth.api";
import CircleIcon from "../components/Icons/CircleIcon.png";
import TrendingIcon from "../components/Icons/TrendingIcon.png";
import SecureIcon from "../components/Icons/SecureIcon.png";
import { alpha } from "@mui/material/styles";

export default function EnhancedLogin() {
  const [email, setEmail] = useState("admin@gmail.com");
  const [password, setPassword] = useState("1");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [remember, setRemember] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const carouselData = [
    {
      icon: <img src={CircleIcon} alt="Secure" width={56} height={64} />,
      title: "Secure CRM Access",
      description:
        "Industry-standard encryption protecting your organization's sensitive data and corporate insights.",
    },
    {
      icon: <img src={SecureIcon} alt="Insights" width={54} height={64} />,
      title: "Secure & Reliable CRM",
      description:
        "Top-tier data security and encrypted communications to keep your business and client information safe.",
    },
    {
      icon: <img src={TrendingIcon} alt="Insights" width={58} height={64} />,
      title: "Advanced Business Insights",
      description:
        "Real-time analytics and AI-driven reports to help you make data-backed decisions for your business growth.",
    },
  ];

  // Extended slides array with first slide cloned at the end for seamless looping
  const extendedSlides = [...carouselData, carouselData[0]];
  const totalSlides = extendedSlides.length;

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => prev + 1);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Handle seamless reset when reaching the cloned slide
  useEffect(() => {
    if (activeSlide === carouselData.length) {
      // Wait for the transition to complete, then reset to slide 0 without animation
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setActiveSlide(0);
      }, 600); // Match the transition duration

      return () => clearTimeout(timeout);
    }
  }, [activeSlide, carouselData.length]);

  // Re-enable transition after resetting position
  useEffect(() => {
    if (!isTransitioning && activeSlide === 0) {
      // Small delay to ensure the position reset happens first
      const timeout = setTimeout(() => {
        setIsTransitioning(true);
      }, 50);

      return () => clearTimeout(timeout);
    }
  }, [isTransitioning, activeSlide]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await loginApi({ email, password });
      const res = await getProfileApi();
      login(res.data.data);
      navigate("/");
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Invalid login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "90vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      {/* <Fade in={true} timeout={800}> */}
      <Paper
        elevation={24}
        sx={{
          width: { xs: "95%", sm: 480, md: 900 },
          height: { xs: "auto", md: 520 },
          borderRadius: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          boxShadow: "0 20px 80px rgba(0,0,0,0.2)",
        }}
      >
        {/* Left: Login Form */}
        {/* <Slide direction="right" in={true} timeout={600}> */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 4, md: 5 },
            display: "flex",
            flexDirection: "column",
            bgcolor: "#ffffff",
          }}
        >
          {/* Logo and Brand */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
            <Avatar
              sx={{
                 color: "primary.main",
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                width: 36,
                height: 36,
                borderRadius: 3,
              }}
            >
              <LockOutlined sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography
              fontSize="16px"
              fontWeight={600}
              sx={{ color: "#2d3748", letterSpacing: 0.3 }}
            >
              ARA Discoveries
            </Typography>
          </Box>

          {/* Title */}
          <Typography
            fontSize="30px"
            fontWeight={700}
            sx={{
              color: "#1a202c",
              mb: 0.5,
              letterSpacing: -0.5,
            }}
          >
            Hello,
          </Typography>
          <Typography
            fontSize="20px"
            fontWeight={400}
            sx={{
              color: "#4B5563",
              mb: 3,
              letterSpacing: -0.5,
            }}
          >
            Welcome Back
          </Typography>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <TextField
              fullWidth
              variant="standard"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                mb: 3,
                "& input": {
                  fontSize: "18px",
                  padding: "12px 0",
                  color: "#1f2937",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "2px solid #e5e7eb",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "2px solid #c7d2fe",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "2px solid #6366f1",
                },
                "& ::placeholder": {
                  color: "#9ca3af",
                  opacity: 1,
                  fontSize: "16px",
                },
              }}
            />

            <TextField
              fullWidth
              variant="standard"
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                mb: 2,
                "& input": {
                  fontSize: "18px",
                  padding: "12px 0",
                  color: "#1f2937",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "2px solid #e5e7eb",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "2px solid #c7d2fe",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "2px solid #6366f1",
                },
                "& ::placeholder": {
                  color: "#9ca3af",
                  opacity: 1,
                  fontSize: "16px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      sx={{ color: "#9ca3af" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Remember  */}

            <FormControlLabel
              control={
                <Checkbox
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  size="small"
                  sx={{
                    color: "#cbd5e0",
                    "&.Mui-checked": {
                      color: "#667eea",
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{ color: "#4a5568", fontSize: "0.875rem" }}
                >
                  Remember me
                </Typography>
              }
            />
            {/* Sign In Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{
                py: 1.5,
                mt: 3,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "0.9375rem",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                background: "primary.main",
                // boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "primary.main",
                  // boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&.Mui-disabled": {
                  background: "#cbd5e0",
                },
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Sign In"
              )}
            </Button>
          </Box>
        </Box>
        {/* </Slide> */}

        {/* Right: Carousel Panel */}
        {/* <Slide direction="left" in={true} timeout={600}> */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
            p: 5,
            overflow: "hidden",
          }}
        >
          {/* Carousel Content */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: 400,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: `${totalSlides * 100}%`,
                transform: `translateX(-${activeSlide * (100 / totalSlides)}%)`,
                transition: isTransitioning ? "transform 0.6s ease-in-out" : "none",
              }}
            >
              {extendedSlides.map((slide, index) => (
                <Box
                  key={index}
                  sx={{
                    width: `${100 / totalSlides}%`,
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.15)",
                      backdropFilter: "blur(10px)",
                      borderRadius: 4,
                      p: 5,
                      textAlign: "center",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      // boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                      // animation: "float 3s ease-in-out infinite",
                      "@keyframes float": {
                        "0%, 100%": { transform: "translateY(0px)" },
                        "50%": { transform: "translateY(-10px)" },
                      },
                    }}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        // borderRadius: "50%",
                        // bgcolor: "rgba(255, 255, 255, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 32px",
                        // border: "2px solid rgba(255, 255, 255, 0.3)",
                      }}
                    >
                      {slide.icon}
                    </Box>

                    {/* Title */}
                    <Typography
                      fontSize="26px"
                      fontWeight={700}
                      sx={{ color: "#ffffff", mb: 2 }}
                    >
                      {slide.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      fontSize="15px"
                      sx={{
                        color: "rgba(255,255,255,0.9)",
                        lineHeight: 1.7,
                        mb: 4,
                        px: 1,
                      }}
                    >
                      {slide.description}
                    </Typography>

                    {/* Progress Dots */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 1.5,
                      }}
                    >
                      {carouselData.map((_, dotIndex) => {
                        // When at the cloned slide (index 3), highlight dot 0
                        const isActive = activeSlide === carouselData.length
                          ? dotIndex === 0
                          : dotIndex === activeSlide;
                        return (
                          <Box
                            key={dotIndex}
                            onClick={() => setActiveSlide(dotIndex)}
                            sx={{
                              width: isActive ? 40 : 32,
                              height: 4,
                              borderRadius: 2,
                              bgcolor: isActive
                                ? "#ffffff"
                                : "rgba(255, 255, 255, 0.4)",
                              transition: "all 0.3s ease",
                              cursor: "pointer",
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Decorative circles
              <Box
                sx={{
                  position: "absolute",
                  top: 40,
                  right: 40,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": {
                      opacity: 0.6,
                      transform: "scale(1)",
                    },
                    "50%": {
                      opacity: 0.8,
                      transform: "scale(1.05)",
                    },
                  },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 60,
                  left: 50,
                  width: 50,
                  height: 50,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  animation: "pulse 2s ease-in-out infinite 0.5s",
                }}
              /> */}
        </Box>
        {/* </Slide> */}
      </Paper>
      {/* </Fade> */}
    </Box>
  );
}
